/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cityStatePostal, hasAddress, presentField } from './helpers';
import type { SomAddress, SomOrderModel } from '@/lib/order/guest-lookup.server';

export interface OrderInfoCardProps {
    order: SomOrderModel;
}

/** A titled block with lines; renders nothing when there is nothing to show. */
function InfoSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <p className="text-sm font-medium text-foreground">{title}</p>
            <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">{children}</div>
        </div>
    );
}

/** Name + street + city/state/postal + phone lines for an address, skipping missing parts. */
function AddressLines({ address, name }: { address: SomAddress; name?: string }) {
    const fullName =
        presentField(name) ?? presentField([address.firstName, address.lastName].filter(Boolean).join(' '));
    const street = presentField(address.street);
    const region = cityStatePostal(address);
    const phone = presentField(address.phone);

    return (
        <>
            {fullName ? <p>{fullName}</p> : null}
            {street ? <p>{street}</p> : null}
            {region ? <p>{region}</p> : null}
            {phone ? <p>{phone}</p> : null}
        </>
    );
}

/**
 * Delivery method, shipping address, billing address, and payment summary. Mirrors SFRA's
 * `order/orderDetails` info blocks. `shipping` and `billing` are nullable in the SOM model
 * (only populated once the order has an account + delivery method), so each block is guarded
 * and the card renders only the sections that carry data.
 */
export function OrderInfoCard({ order }: OrderInfoCardProps) {
    const { t } = useTranslation('trackOrder');

    const shipping = order.shipping;
    const deliveryMethodName = presentField(shipping?.deliveryMethodName);
    const deliveryMethodDescription = presentField(shipping?.deliveryMethodDescription);
    const shippingAddress = hasAddress(shipping?.deliverToAddress) ? shipping.deliverToAddress : undefined;

    const billingAddress = order.billing?.address;
    const hasBillingAddress = hasAddress(billingAddress);

    const payment = order.billing?.payment;
    const cardNumber = presentField(payment?.DisplayCardNumber);
    const cardType = presentField(payment?.CardType) ?? presentField(payment?.CardCategory);
    const expiryMonth = presentField(payment?.ExpiryMonth);
    const expiryYear = presentField(payment?.ExpiryYear);

    const sections: React.ReactNode[] = [];

    if (deliveryMethodName || deliveryMethodDescription) {
        sections.push(
            <InfoSection key="delivery" title={t('sections.shippingMethod')}>
                {deliveryMethodName ? <p>{deliveryMethodName}</p> : null}
                {deliveryMethodDescription ? <p>{deliveryMethodDescription}</p> : null}
            </InfoSection>
        );
    }

    if (shippingAddress) {
        sections.push(
            <InfoSection key="shipping" title={t('sections.shippingAddress')}>
                <AddressLines address={shippingAddress} name={shipping?.deliverToName} />
            </InfoSection>
        );
    }

    if (hasBillingAddress) {
        sections.push(
            <InfoSection key="billing" title={t('sections.billingAddress')}>
                <AddressLines address={billingAddress} />
            </InfoSection>
        );
    }

    if (cardNumber || cardType) {
        sections.push(
            <InfoSection key="payment" title={t('sections.payment')}>
                <p>{[cardType, cardNumber].filter(Boolean).join(' ')}</p>
                {expiryMonth && expiryYear ? (
                    <p>{t('payment.endingIn', { month: expiryMonth, year: expiryYear })}</p>
                ) : null}
            </InfoSection>
        );
    }

    if (sections.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">{t('sections.orderInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-6 sm:grid-cols-2">{sections}</CardContent>
        </Card>
    );
}
